// Moving things in the sidebar by dragging the rows themselves: no handles. A press that does
// not move stays a click; touch screens keep their press for scrolling and their long press
// for the menu. What is moved lifts and follows the pointer, and always settles into its place
// with one short glide: never a jump, never a flash.
import { useRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from "react";
import { flushSync } from "react-dom";

/** How far a press must travel before it is a drag. */
const SLOP = 5;
/** How long the dropped row takes to settle (app.css .settle). */
const SETTLE_MS = 220;

const pressable = (e: ReactPointerEvent) => e.button === 0 && e.pointerType !== "touch";

/** Pointer moves until the press ends, then [end]. */
function follow(onMove: (e: PointerEvent) => void, end: () => void) {
  const up = () => { removeEventListener("pointermove", onMove); removeEventListener("pointerup", up); removeEventListener("pointercancel", up); end(); };
  addEventListener("pointermove", onMove); addEventListener("pointerup", up); addEventListener("pointercancel", up);
}

/**
 * The lifted row glides from where it was let go ([from], a transform off its slot) into its
 * slot, its raised look fading as it lands. [quiet] holds the list still meanwhile: the row the
 * pointer happens to rest on does not light up while the dropped one is still landing.
 */
function settle(el: HTMLElement, from: string, quiet?: HTMLElement | null) {
  el.classList.add("lift");
  el.style.transform = from;
  void el.offsetHeight;
  el.classList.add("settle"); el.style.transform = "";
  quiet?.classList.add("settling");
  // by time, not transitionend: a row dropped exactly on its slot has nothing to animate
  setTimeout(() => { el.classList.remove("lift", "settle"); quiet?.classList.remove("settling"); }, SETTLE_MS);
}

/**
 * Where an item dragged [dy] from its place lands among [boxes]: past every item whose middle its
 * leading edge has crossed (its bottom going down, its top going up).
 */
export function landing(boxes: readonly { top: number; h: number }[], from: number, dy: number): number {
  const me = boxes[from]!, top = me.top + dy, bottom = top + me.h;
  let to = from;
  for (let j = from + 1; j < boxes.length; j++) if (bottom > boxes[j]!.top + boxes[j]!.h / 2) to = j;
  for (let j = from - 1; j >= 0; j--) if (top < boxes[j]!.top + boxes[j]!.h / 2) to = j;
  return to;
}

/**
 * Reordering a list by dragging an item: it lifts and follows the pointer while the others slide
 * aside (items may differ in height, like categories with their sources showing), and settles
 * into its place when let go. Items are the box's children marked `data-drag`; a press anywhere
 * inside one can start it. Alt and an arrow key move the focused item.
 */
export function useDragOrder(move: (from: number, to: number) => void) {
  const box = useRef<HTMLDivElement>(null);
  const moved = useRef(false); // the click that ends a drag opens nothing
  const items = () => [...box.current!.querySelectorAll<HTMLElement>(":scope > [data-drag]")];

  const start = (e: ReactPointerEvent) => {
    if (!pressable(e)) return;
    const el = (e.currentTarget as HTMLElement).closest<HTMLElement>("[data-drag]")!;
    const all = items(), from = all.indexOf(el);
    if (from < 0) return;
    const boxes = all.map((r) => ({ top: r.offsetTop, h: r.offsetHeight }));
    const me = boxes[from]!;
    const gap = all.length > 1 ? boxes[1]!.top - boxes[0]!.top - boxes[0]!.h : 0;
    const room = me.h + gap; // what the others move by to make space
    const y0 = e.clientY;
    let dy = 0, to = from, on = false;
    moved.current = false;

    follow((ev) => {
      dy = ev.clientY - y0;
      if (!on) {
        if (Math.abs(dy) < SLOP) return;
        on = true; moved.current = true;
        box.current!.classList.add("dragging"); el.classList.add("lift");
      }
      const last = boxes[boxes.length - 1]!;
      dy = Math.max(boxes[0]!.top - me.top, Math.min(last.top + last.h - me.top - me.h, dy));
      to = landing(boxes, from, dy);
      el.style.transform = `translateY(${dy}px)`;
      all.forEach((r, j) => {
        if (r === el) return;
        const shift = from < j && j <= to ? -room : to <= j && j < from ? room : 0;
        r.style.transform = shift ? `translateY(${shift}px)` : "";
      });
    }, () => {
      if (!on) return;
      // the new order and the others' places change in one frame, with no transition
      box.current!.classList.remove("dragging");
      flushSync(() => move(from, to));
      for (const r of all) if (r !== el) r.style.transform = "";
      settle(el, `translateY(${me.top + dy - el.offsetTop}px)`, box.current);
    });
  };

  const key = (e: ReactKeyboardEvent, from: number, focus: string) => {
    if (!e.altKey || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) return;
    e.preventDefault();
    flushSync(() => move(from, from + (e.key === "ArrowUp" ? -1 : 1)));
    box.current?.querySelector<HTMLElement>(`[data-drag="${CSS.escape(focus)}"] button:not(.chev), button[data-drag="${CSS.escape(focus)}"]`)?.focus();
  };

  return { box, start, key, swallow: () => { const m = moved.current; moved.current = false; return m; } };
}

/**
 * Carrying a row into a group: it lifts and follows the pointer; the group under the pointer (an
 * element with `data-drop`) lights up and [over] says so, so the list can show where the row
 * would land. Let go over a group and [drop] moves it there: the row glides from under the
 * pointer into its new place (found by its `data-carry` id). Let go anywhere else and it glides home.
 */
export function useDragInto({ over, drop }: { over: (id: string, into: string | null) => void; drop: (id: string, into: string) => void }) {
  const moved = useRef(false);

  const start = (e: ReactPointerEvent, id: string, home: string) => {
    if (!pressable(e)) return;
    const el = e.currentTarget as HTMLElement;
    const x0 = e.clientX, y0 = e.clientY;
    let on = false, target: HTMLElement | null = null;
    moved.current = false;
    const aim = (t: HTMLElement | null) => {
      if (t === target) return;
      target?.classList.remove("drop"); t?.classList.add("drop");
      target = t;
      over(id, t?.dataset.drop ?? null);
    };

    follow((ev) => {
      const dx = ev.clientX - x0, dy = ev.clientY - y0;
      if (!on) {
        if (Math.hypot(dx, dy) < SLOP) return;
        on = true; moved.current = true;
        el.classList.add("lift", "carry"); document.body.classList.add("carrying");
      }
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      // the row itself lets the pointer through (.carry), so what is under it can be found
      const t = document.elementFromPoint(ev.clientX, ev.clientY)?.closest<HTMLElement>("[data-drop]") ?? null;
      aim(t && t.dataset.drop !== home ? t : null);
    }, () => {
      if (!on) return;
      document.body.classList.remove("carrying");
      const into = target?.dataset.drop;
      const was = el.getBoundingClientRect();
      aim(null);
      if (!into) {
        el.classList.remove("carry");
        const from = el.style.transform;
        el.style.transform = "";
        settle(el, from);
        return;
      }
      // the row moves to its new group in this frame, where its preview was, and glides there from the pointer
      flushSync(() => drop(id, into));
      const landed = document.querySelector<HTMLElement>(`[data-carry="${CSS.escape(id)}"]`);
      if (!landed) return;
      const now = landed.getBoundingClientRect();
      settle(landed, `translate(${was.left - now.left}px, ${was.top - now.top}px)`, landed.closest<HTMLElement>(".cats"));
    });
  };

  return { start, swallow: () => { const m = moved.current; moved.current = false; return m; } };
}
